import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  AppState,
  ScrollView,
  Linking,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import DeviceInfo from "react-native-device-info";
import {
  checkMultiple,
  request,
  openSettings,
  PERMISSIONS,
  RESULTS,
  type Permission,
  type PermissionStatus,
} from "react-native-permissions";
import colors from "@constants/color";
import MovexGeo from "@movexgeo/react-native-movex";
import { setItem } from "@lib/storage";
import {
  BATTERY_KEY,
  BATTERY_REQUESTED_KEY,
  POST_NOTIFICATIONS,
  type PermissionItem,
} from "./appPermissions";

type Props = {
  visible: boolean;
  items: PermissionItem[];
  onResolved: (granted: boolean) => void;
};

function isGranted(status?: PermissionStatus | string) {
  return status === RESULTS.GRANTED || status === RESULTS.LIMITED;
}

const META: Record<string, { icon: string; description: string }> = {
  [PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION]: {
    icon: "📍",
    description:
      "Used to show nearby features and provide accurate mapping services.",
  },
  [PERMISSIONS.ANDROID.ACCESS_COARSE_LOCATION]: {
    icon: "🧭",
    description:
      "Helps with general location-based services without pinpoint accuracy.",
  },
  [PERMISSIONS.ANDROID.ACCESS_BACKGROUND_LOCATION]: {
    icon: "🕒",
    description:
      "Allows the app to provide features even when not actively in use.",
  },
  [PERMISSIONS.ANDROID.ACTIVITY_RECOGNITION]: {
    icon: "🚶",
    description:
      "Saves battery by pausing GPS when you're parked and resuming when you start moving.",
  },
  [POST_NOTIFICATIONS]: {
    icon: "🔔",
    description: "Stay informed about important updates and order activity.",
  },
  [PERMISSIONS.ANDROID.CAMERA]: {
    icon: "📷",
    description: "Needed for scanning QR codes and uploading profile photos.",
  },
  [PERMISSIONS.ANDROID.READ_MEDIA_IMAGES]: {
    icon: "🖼️",
    description: "Required to access your photos for uploading.",
  },
  [PERMISSIONS.ANDROID.READ_MEDIA_VIDEO]: {
    icon: "🎬",
    description: "Required to access your videos for uploading.",
  },
  [PERMISSIONS.ANDROID.READ_MEDIA_VISUAL_USER_SELECTED]: {
    icon: "🖼️",
    description: "Allows access to specific photos you choose to share.",
  },
  [PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE]: {
    icon: "📁",
    description: "Required to access your media and files for uploading.",
  },
  [PERMISSIONS.ANDROID.WRITE_EXTERNAL_STORAGE]: {
    icon: "💾",
    description: "Necessary for saving files and exports to your device.",
  },
  [BATTERY_KEY]: {
    icon: "🔋",
    description: "Ensures consistent performance and timely notifications.",
  },
};

const FALLBACK_META = { icon: "🔧", description: "" };

const PermissionsModal: React.FC<Props> = ({ visible, items, onResolved }) => {
  const [statuses, setStatuses] = useState<
    Record<string, PermissionStatus | string>
  >({});
  const [requesting, setRequesting] = useState(false);
  const [blockedKeys, setBlockedKeys] = useState<Set<string>>(new Set());
  const appStateRef = useRef(AppState.currentState);
  // Prevent re-triggering the OEM battery dialog in the same session.
  // Funtouch OS / MIUI show a system dialog on every requestIgnoreBatteryOptimizations
  // call, and isIgnoringBatteryOptimizations() may stay false even after the user
  // grants it via OEM settings, causing an infinite request loop.
  const batteryRequestedRef = useRef(false);

  const openSettingsForKey = useCallback((key: string) => {
    if (key === BATTERY_KEY) {
      MovexGeo.openOemBatterySettings().catch(() => {});
      return;
    }
    if (Platform.OS === "android" && key === POST_NOTIFICATIONS) {
      Linking.sendIntent("android.settings.APP_NOTIFICATION_SETTINGS", [
        {
          key: "android.provider.extra.APP_PACKAGE",
          value: DeviceInfo.getBundleId(),
        },
      ]).catch(() => openSettings().catch(() => {}));
      return;
    }
    openSettings().catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    if (!items.length) {
      onResolved(true);
      return;
    }
    const keys = items
      .filter((p) => p.key !== BATTERY_KEY)
      .map((p) => p.key as Permission);
    const result = await checkMultiple(keys);
    let batteryGranted = false;
    try {
      batteryGranted = await MovexGeo.isIgnoringBatteryOptimizations();
    } catch {
      // API throws on devices that don't support battery optimization exemption.
      // Treat as not applicable — don't block navigation.
      batteryGranted = true;
    }
    const merged: Record<string, PermissionStatus | string> = {
      ...result,
      [BATTERY_KEY]: batteryGranted ? RESULTS.GRANTED : RESULTS.DENIED,
    };
    setStatuses(merged);
    setBlockedKeys((prev) => {
      const updated = new Set(prev);
      for (const key of prev) {
        if (isGranted(merged[key])) updated.delete(key);
      }
      return updated.size !== prev.size ? updated : prev;
    });

    const nonBatteryGranted = items
      .filter((p) => p.key !== BATTERY_KEY)
      .every((p) => isGranted(merged[p.key]));
    // Proceed if battery is granted, OR if a request was already attempted
    // (handles OEM devices where isIgnoringBatteryOptimizations always returns false).
    if (nonBatteryGranted && (batteryGranted || batteryRequestedRef.current))
      onResolved(true);
  }, [items, onResolved]);

  useEffect(() => {
    if (visible) refresh();
  }, [visible, refresh]);

  useEffect(() => {
    if (!visible) return;
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (
        appStateRef.current.match(/inactive|background/) &&
        nextAppState === "active"
      ) {
        refresh();
      }
      appStateRef.current = nextAppState;
    });
    return () => subscription.remove();
  }, [visible, refresh]);

  const onGrant = async () => {
    try {
      setRequesting(true);
      const allKeys = items
        .filter((p) => p.key !== BATTERY_KEY)
        .map((p) => p.key as Permission);
      const keysToRequest = allKeys.filter((key) => !isGranted(statuses[key]));
      const REQUEST_TIMEOUT_MS = 60000;
      const DELAY_BETWEEN_REQUESTS_MS = 500;
      const newBlocked = new Set(blockedKeys);
      for (const permission of keysToRequest) {
        const requestPromise = request(permission);
        const timeoutPromise = new Promise<PermissionStatus>((_, reject) =>
          setTimeout(
            () => reject(new Error("Permission request timeout")),
            REQUEST_TIMEOUT_MS,
          ),
        );
        try {
          const result = await Promise.race([requestPromise, timeoutPromise]);
          if (result === RESULTS.BLOCKED) {
            newBlocked.add(permission);
          } else if (isGranted(result)) {
            newBlocked.delete(permission);
          }
        } catch {
          // timeout
        }
        await new Promise((r) => setTimeout(r, DELAY_BETWEEN_REQUESTS_MS));
      }
      setBlockedKeys(newBlocked);
      if (
        statuses[BATTERY_KEY] !== RESULTS.GRANTED &&
        !batteryRequestedRef.current
      ) {
        batteryRequestedRef.current = true;
        try {
          await MovexGeo.requestIgnoreBatteryOptimizations();
        } catch {}
        // Persist so the gate doesn't re-show the modal on subsequent boots
        // for OEM devices that can never report isIgnoringBatteryOptimizations=true.
        setItem(BATTERY_REQUESTED_KEY, "1").catch(() => {});
      }
    } finally {
      setRequesting(false);
      refresh();
    }
  };

  if (!visible) return null;

  const hasBlocked = blockedKeys.size > 0;

  return (
    <Modal
      visible
      animationType="fade"
      presentationStyle="fullScreen"
      transparent={false}
      statusBarTranslucent
      navigationBarTranslucent
    >
      <SafeAreaView style={styles.safe} edges={["top", "bottom"]}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.title}>Permissions required</Text>
          <Text style={styles.subtitle}>
            To use the app fully, allow access to location, notifications,
            camera, and photos.
          </Text>

          <View style={styles.list}>
            {items.map((item) => {
              const status = statuses[item.key];
              const ok = isGranted(status);
              const isLimited = status === RESULTS.LIMITED;
              const isBlocked =
                status === RESULTS.BLOCKED || blockedKeys.has(item.key);
              const meta = META[item.key] ?? FALLBACK_META;
              const onSettingsPress = () => openSettingsForKey(item.key);

              return (
                <View
                  key={item.key}
                  style={[styles.card, isBlocked && styles.cardBlocked]}
                >
                  <View
                    style={[
                      styles.iconBox,
                      isLimited && styles.iconBoxLimited,
                      isBlocked && styles.iconBoxBlocked,
                      ok && !isLimited && styles.iconBoxOk,
                    ]}
                  >
                    <Text style={styles.iconText}>{meta.icon}</Text>
                  </View>
                  <View style={styles.cardBody}>
                    <View style={styles.cardHeader}>
                      <Text style={styles.cardTitle}>{item.label}</Text>
                      {ok && !isLimited && (
                        <View style={[styles.badge, styles.badgeGranted]}>
                          <Text style={styles.badgeTextGranted}>GRANTED</Text>
                        </View>
                      )}
                      {isLimited && (
                        <View style={[styles.badge, styles.badgeLimited]}>
                          <Text style={styles.badgeTextLimited}>LIMITED</Text>
                        </View>
                      )}
                      {isBlocked && (
                        <View style={[styles.badge, styles.badgeBlocked]}>
                          <Text style={styles.badgeTextBlocked}>BLOCKED</Text>
                        </View>
                      )}
                      {!ok && !isLimited && !isBlocked && (
                        <View style={[styles.badge, styles.badgeDenied]}>
                          <Text style={styles.badgeTextDenied}>DENIED</Text>
                        </View>
                      )}
                    </View>
                    {!!meta.description && (
                      <Text style={styles.cardDescription}>
                        {meta.description}
                      </Text>
                    )}
                  </View>
                  {isBlocked && (
                    <TouchableOpacity
                      onPress={onSettingsPress}
                      style={styles.settingsIcon}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Text style={styles.settingsIconText}>⚙</Text>
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
          </View>

          {hasBlocked && (
            <Text style={styles.hint}>
              Tap the ⚙ icon next to a blocked permission to open its settings
              and allow access. The app updates automatically when you return.
            </Text>
          )}
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            onPress={onGrant}
            style={[
              styles.btn,
              styles.primary,
              requesting && styles.btnDisabled,
            ]}
            disabled={requesting}
            activeOpacity={0.85}
          >
            <Text style={styles.btnText}>
              {requesting ? "Requesting…" : "Grant permissions"}
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );
};

const CARD_BG = colors.white;
const SCREEN_BG = "#FBF4F6";
const ACCENT = colors.primary; // #E40046
const ACCENT_TINT = "#FDE5EC";
const LIMITED_BG = "#FCE6CF";
const LIMITED_FG = "#B25E15";
const SUCCESS_TINT = "#E6F5EA";
const SUCCESS_FG = "#1F8A3C";
const DANGER_TINT = "#FDE3E1";
const DANGER_FG = "#C41A16";
const BORDER = "#F1E3E8";

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: SCREEN_BG },
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    color: colors.black,
    textAlign: "center",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: colors.Gray40,
    textAlign: "center",
    lineHeight: 20,
    marginBottom: 20,
    paddingHorizontal: 12,
  },
  list: { marginTop: 4 },
  card: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: CARD_BG,
    borderRadius: 14,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: BORDER,
  },
  cardBlocked: {
    borderColor: DANGER_TINT,
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: ACCENT_TINT,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  iconBoxOk: { backgroundColor: SUCCESS_TINT },
  iconBoxLimited: { backgroundColor: LIMITED_BG },
  iconBoxBlocked: { backgroundColor: DANGER_TINT },
  iconText: { fontSize: 18 },
  cardBody: { flex: 1 },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 2,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: "700",
    color: colors.black,
    marginRight: 8,
  },
  cardDescription: {
    fontSize: 13,
    color: colors.Gray40,
    lineHeight: 18,
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  badgeGranted: { backgroundColor: SUCCESS_TINT },
  badgeTextGranted: {
    color: SUCCESS_FG,
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.4,
  },
  badgeLimited: { backgroundColor: LIMITED_FG },
  badgeTextLimited: {
    color: colors.white,
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.4,
  },
  badgeBlocked: { backgroundColor: DANGER_FG },
  badgeTextBlocked: {
    color: colors.white,
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.4,
  },
  badgeDenied: { backgroundColor: "#EEF0F4" },
  badgeTextDenied: {
    color: "#5C5C5C",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.4,
  },
  settingsIcon: { marginLeft: 8, padding: 4 },
  settingsIconText: { fontSize: 20, color: colors.darkGrayBlue },
  hint: {
    fontSize: 12,
    color: colors.Gray40,
    marginTop: 8,
    lineHeight: 18,
    textAlign: "center",
  },
  footer: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 16,
    backgroundColor: SCREEN_BG,
    borderTopWidth: 1,
    borderTopColor: BORDER,
  },
  btn: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
  },
  primary: { backgroundColor: ACCENT },
  btnDisabled: { opacity: 0.7 },
  btnText: { color: colors.white, fontWeight: "700", fontSize: 15 },
});

export default PermissionsModal;
